// ── GuidedTour — 인터랙티브 가이드 투어 (스포트라이트 + 깜빡 ring + 풍선 힌트) ──
// 사용법:
//   <GuidedTour steps={STEPS} onTabChange={setTab} onClose={...} />
// 각 step:
//   { selector: '[data-tour="auto-fetch"]', message, position?, tab? }
//   - selector: DOM 쿼리. 그 element에 ring + tooltip 표시
//   - message: 풍선에 표시할 안내문
//   - position: 'top' | 'bottom' | 'left' | 'right' | 'auto' (기본: auto, 하단 우선)
//   - tab: 이 step 시작 전 이 탭으로 자동 전환 (option)
// 사용자가 그 element를 클릭하면 → 자동 다음 step (또는 "다음" 버튼)
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useLang } from '../i18n/LangContext';   // V147 중국어 화면이면 중국어 투어

const PADDING = 8;
const TOOLTIP_GAP = 14;
const SCROLL_PADDING = 100;

export default function GuidedTour({ steps: stepsIn = [], onTabChange, onClose, currentTab, availableTabs }) {
  const { lang } = useLang();
  const L = (ko, zh) => (lang === 'zh' ? zh : ko);
  // V147: 중국어 화면은 중국 매장 직원용 투어, 이 계정이 못 보는 탭은 건너뛴다
  const tabsKey = Array.isArray(availableTabs) ? availableTabs.join(',') : '';
  const steps = useMemo(
    () => tourStepsFor(stepsIn === SEMFIT_TOUR && lang === 'zh' ? SEMFIT_TOUR_ZH : stepsIn, tabsKey ? tabsKey.split(',') : null),
    [stepsIn, lang, tabsKey]
  );
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0, placement: 'bottom' });
  const [pending, setPending] = useState(false);  // 탭 전환 + element 등장 대기
  const lastSelectorRef = useRef(null);

  const total = steps.length;
  const cur = steps[Math.min(stepIdx, Math.max(0, steps.length - 1))];

  const next = () => {
    if (stepIdx >= total - 1) finish();
    else setStepIdx(i => i + 1);
  };
  const prev = () => setStepIdx(i => Math.max(0, i - 1));
  const finish = () => onClose?.();

  // 탭 전환 처리 (필요시)
  useEffect(() => {
    if (!cur) return;
    if (cur.tab && cur.tab !== currentTab && onTabChange) {
      setPending(true);
      onTabChange(cur.tab);
    } else {
      setPending(false);
    }
  }, [stepIdx, cur, currentTab, onTabChange]);

  // element 위치 계산 (selector → DOMRect)
  useEffect(() => {
    if (!cur) return;
    let cancelled = false;

    const tryFind = (attempt = 0) => {
      if (cancelled) return;
      const el = document.querySelector(cur.selector);
      if (el) {
        // 화면 안으로 스크롤
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        // 스크롤 끝나기 기다린 후 위치 측정
        setTimeout(() => {
          if (cancelled) return;
          updateRect(el);
          setPending(false);
        }, 350);
        // V44: capture: true 제거 — React SyntheticEvent와 충돌 회피.
        // bubble 단계에서 React onClick 후 GuidedTour next() 동작.
        const handler = () => {
          setTimeout(() => { if (!cancelled) next(); }, 200);
        };
        el.addEventListener('click', handler, { once: true });
        lastSelectorRef.current = { el, handler };
        return;
      }
      if (attempt > 30) {
        // 1.5초 대기 후 실패 → 그냥 진행 가능 상태
        setPending(false);
        setRect(null);
        return;
      }
      setTimeout(() => tryFind(attempt + 1), 50);
    };
    tryFind();

    return () => {
      cancelled = true;
      const last = lastSelectorRef.current;
      if (last) {
        try { last.el.removeEventListener('click', last.handler); } catch {}
        lastSelectorRef.current = null;
      }
    };
  }, [stepIdx, cur, currentTab]);

  // window resize/scroll 시 위치 재계산
  useEffect(() => {
    const onChange = () => {
      const el = lastSelectorRef.current?.el;
      if (el) updateRect(el);
    };
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, { passive: true });
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange);
    };
  }, []);

  function updateRect(el) {
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });

    // tooltip 위치 결정
    // V45: tooltipH 120 → 220. 실제 박스(제목+메시지+버튼+안내) ~200px.
    // 120 가정 시 'top' placement에서 target과 박스 영역이 겹쳐 클릭 차단됨.
    const tooltipW = 300;
    const tooltipH = 220;
    const desired = cur?.position || 'auto';
    let placement = desired === 'auto' ? 'bottom' : desired;

    let top, left;
    const fitsBottom = r.bottom + TOOLTIP_GAP + tooltipH < window.innerHeight - 20;
    const fitsTop = r.top - TOOLTIP_GAP - tooltipH > 20;
    const fitsRight = r.right + TOOLTIP_GAP + tooltipW < window.innerWidth - 20;
    const fitsLeft = r.left - TOOLTIP_GAP - tooltipW > 20;

    if (placement === 'auto') {
      if (fitsBottom) placement = 'bottom';
      else if (fitsTop) placement = 'top';
      else if (fitsRight) placement = 'right';
      else placement = 'left';
    }

    if (placement === 'bottom') {
      top = r.bottom + TOOLTIP_GAP;
      left = Math.max(12, Math.min(r.left + r.width / 2 - tooltipW / 2, window.innerWidth - tooltipW - 12));
    } else if (placement === 'top') {
      top = r.top - TOOLTIP_GAP - tooltipH;
      left = Math.max(12, Math.min(r.left + r.width / 2 - tooltipW / 2, window.innerWidth - tooltipW - 12));
    } else if (placement === 'right') {
      top = Math.max(12, r.top + r.height / 2 - tooltipH / 2);
      left = r.right + TOOLTIP_GAP;
    } else { // left
      top = Math.max(12, r.top + r.height / 2 - tooltipH / 2);
      left = Math.max(12, r.left - TOOLTIP_GAP - tooltipW);
    }
    setTooltipPos({ top, left, placement });
  }

  if (!cur) return null;

  const ringStyle = rect
    ? {
        position: 'fixed',
        top: rect.top - PADDING,
        left: rect.left - PADDING,
        width: rect.width + PADDING * 2,
        height: rect.height + PADDING * 2,
        borderRadius: 12,
        border: '3px solid #FF6F0F',
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.45)',
        pointerEvents: 'none',
        zIndex: 10000,
        animation: 'tour-pulse 1.4s ease-in-out infinite',
      }
    : null;

  // V46: outer wrapper는 pointer-events: none (target click 통과), inner box만 auto
  const tooltipStyle = {
    position: 'fixed',
    top: tooltipPos.top,
    left: tooltipPos.left,
    width: 300,
    pointerEvents: 'none',
    zIndex: 10001,
  };
  const tooltipBoxStyle = {
    background: '#fff',
    borderRadius: 12,
    padding: '14px 16px',
    boxShadow: '0 12px 36px rgba(0,0,0,0.25)',
    border: '2px solid #FF6F0F',
    pointerEvents: 'auto',
    position: 'relative',
  };

  return (
    <>
      <style>{`
        @keyframes tour-pulse {
          0%   { box-shadow: 0 0 0 9999px rgba(0,0,0,0.45), 0 0 0 0 rgba(30,107,60,0.6); }
          50%  { box-shadow: 0 0 0 9999px rgba(0,0,0,0.45), 0 0 0 12px rgba(30,107,60,0); }
          100% { box-shadow: 0 0 0 9999px rgba(0,0,0,0.45), 0 0 0 0 rgba(30,107,60,0); }
        }
        @keyframes tour-fade-in {
          from { opacity: 0; transform: scale(0.95); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>

      {/* 백드롭 — 클릭하면 닫힘 (실수 방지 위해 비활성) */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'none',
      }} />

      {/* 스포트라이트 ring */}
      {rect && !pending && <div style={ringStyle} />}

      {/* 로딩 표시 (탭 전환 중) */}
      {(pending || !rect) && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000, pointerEvents: 'none',
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 14, fontWeight: 700,
        }}>
          {pending ? L('⏳ 화면 전환 중...', '⏳ 正在切换页面...') : L('🔍 위치 찾는 중...', '🔍 正在查找位置...')}
        </div>
      )}

      {/* 풍선 힌트 (V46: outer none, inner auto) */}
      {rect && !pending && (
        <div style={tooltipStyle}>
        <div style={{ ...tooltipBoxStyle, animation: 'tour-fade-in 0.25s ease-out' }}>
          <ArrowIndicator placement={tooltipPos.placement} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{
              fontSize: 11, fontWeight: 800, color: '#18181B',
              background: '#FAFAFA', padding: '3px 10px', borderRadius: 12,
              border: '1px solid #F4F4F5',
            }}>
              {stepIdx + 1} / {total}
            </span>
            {cur.title && (
              <strong style={{ fontSize: 13, color: '#111827' }}>{cur.title}</strong>
            )}
          </div>

          <div style={{
            fontSize: 13, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-line',
            marginBottom: 12,
          }}>
            {cur.message}
          </div>

          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {stepIdx > 0 && (
              <button onClick={prev} style={btnSecondary}>{L('← 이전', '← 上一步')}</button>
            )}
            <button onClick={next} style={btnPrimary}>
              {stepIdx === total - 1 ? L('🎉 완료', '🎉 完成') : L('다음 →', '下一步 →')}
            </button>
            <button onClick={finish} style={btnGhost} title={L('투어 종료', '结束导览')}>✕</button>
          </div>
          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 8, textAlign: 'center', lineHeight: 1.5 }}>
            {L('💡 초록 테두리 클릭 또는 위 "다음 →" 버튼으로 진행', '💡 点绿色框，或点上面的「下一步 →」继续')}
          </div>
        </div>
        </div>
      )}
    </>
  );
}

// ── 풍선에 화살표 ──
function ArrowIndicator({ placement }) {
  const base = {
    position: 'absolute',
    width: 0, height: 0,
    borderStyle: 'solid',
  };
  if (placement === 'bottom') {
    return <div style={{ ...base, top: -10, left: '50%', marginLeft: -8,
      borderWidth: '0 8px 10px 8px', borderColor: 'transparent transparent #FF6F0F transparent' }} />;
  }
  if (placement === 'top') {
    return <div style={{ ...base, bottom: -10, left: '50%', marginLeft: -8,
      borderWidth: '10px 8px 0 8px', borderColor: '#FF6F0F transparent transparent transparent' }} />;
  }
  if (placement === 'right') {
    return <div style={{ ...base, left: -10, top: '50%', marginTop: -8,
      borderWidth: '8px 10px 8px 0', borderColor: 'transparent #FF6F0F transparent transparent' }} />;
  }
  return <div style={{ ...base, right: -10, top: '50%', marginTop: -8,
    borderWidth: '8px 0 8px 10px', borderColor: 'transparent transparent transparent #FF6F0F' }} />;
}

// ── 버튼 스타일 ──
const btnPrimary = {
  flex: 1, padding: '7px 12px', borderRadius: 7, border: 'none',
  background: '#FF6F0F', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
};
const btnSecondary = {
  padding: '7px 12px', borderRadius: 7,
  border: '1.5px solid #e5e7eb', background: '#fff',
  color: '#6b7280', fontSize: 12, fontWeight: 600, cursor: 'pointer',
};
const btnGhost = {
  padding: '7px 10px', borderRadius: 7,
  border: 'none', background: '#f3f4f6',
  color: '#6b7280', fontSize: 13, cursor: 'pointer',
};

// ── 셈핏 메인 인터랙티브 투어 시퀀스 ──────────────────────────
// 사용자가 각 element를 직접 클릭하면 다음으로 자동 진행
export const SEMFIT_TOUR = [
  {
    selector: '[data-tour="tab-prices"]',
    title: '1단계 — 단가표',
    message: '👋 환영합니다! 먼저 식재료 단가를 등록해야 모든 원가가 자동 계산돼요.\n\n초록 테두리 부분을 눌러주세요.',
    tab: 'home',
    position: 'bottom',
  },
  {
    selector: '[data-tour="prices-auto-fetch"]',
    title: '시장 가격 자동 조회',
    message: '✨ 이 버튼 한 번이면 KAMIS 도매가 + 네이버 최저가 + AI 매칭이 한 번에 실행돼요.\n\n눌러서 확인해보세요!',
    tab: 'prices',
    position: 'bottom',
  },
  {
    selector: '[data-tour="prices-add-item"]',
    title: '직접 항목 추가',
    message: '👍 잘했어요! 이번엔 새 식재료를 직접 추가하려면 여기를 누르세요.',
    tab: 'prices',
    position: 'bottom',
  },
  {
    selector: '[data-tour="tab-recipes"]',
    title: '2단계 — 레시피',
    message: '이제 메뉴 레시피를 등록할 차례예요. 단가표 가격이 모든 레시피 원가에 자동 반영됩니다.',
    tab: 'prices',
    position: 'bottom',
  },
  {
    selector: '[data-tour="tab-profit"]',
    title: '3단계 — 손익관리',
    message: '매월 매출과 비용을 입력하는 곳이에요. 종소세·대시보드 모두 여기 데이터를 자동으로 끌어가요.',
    tab: 'recipes',
    position: 'bottom',
  },
  {
    selector: '[data-tour="tab-payroll"]',
    title: '4단계 — 인건비',
    message: '직원 등록 → 스케줄 입력 → 급여 자동 계산. 한국 노동법(주휴수당·연장·야간·4대보험) 모두 처리해요.',
    tab: 'profit',
    position: 'bottom',
  },
  {
    selector: '[data-tour="tab-inventory"]',
    title: '5단계 — 재고관리',
    message: '안전재고 입력하면 발주 알림 + 추천 발주량이 자동으로 떠요. 발주서로 한 번에 전송도 가능.',
    tab: 'payroll',
    position: 'bottom',
  },
  {
    selector: '[data-tour="tab-tax"]',
    title: '6단계 — 세무회계',
    message: '부가세·종소세·원천세·거래장부를 한 곳에서. 손익관리·인건비 데이터 자동 연동돼요.',
    tab: 'inventory',
    position: 'bottom',
  },
  {
    selector: '[data-tour="tab-home"]',
    title: '🎉 완료!',
    message: '모든 핵심 기능을 둘러봤어요. 홈으로 돌아가서 매장 운영 한눈에 확인해보세요!\n\n언제든 우상단 "💡 도움말 → 인터랙티브 투어"로 다시 시작 가능해요.',
    tab: 'tax',
    position: 'bottom',
  },
];

// ── V147: 중국어 화면용 투어 — 중국 매장 직원 기준 (단가표 → 발주서 → 레시피 → 재고) ──
export const SEMFIT_TOUR_ZH = [
  {
    selector: '[data-tour="tab-prices"]',
    title: '第1步 — 单价表',
    message: '👋 欢迎！食材的进货价格都在单价表里，配方成本会自动计算。\n\n点一下绿色框。',
    tab: 'home',
    position: 'bottom',
  },
  {
    selector: '[data-tour="prices-add-item"]',
    title: '添加食材',
    message: '要加新的食材，点这里。',
    tab: 'prices',
    position: 'bottom',
  },
  {
    selector: '[data-tour="tab-purchase"]',
    title: '第2步 — 订货单',
    message: '在这里下单：左边点食材、填数量，右边就生成订货单，可以直接发微信给供应商。',
    tab: 'prices',
    position: 'bottom',
  },
  {
    selector: '[data-tour="tab-recipes"]',
    title: '第3步 — 配方',
    message: '每道菜用什么食材、用多少，都在配方里。',
    tab: 'purchase',
    position: 'bottom',
  },
  {
    selector: '[data-tour="tab-inventory"]',
    title: '第4步 — 库存管理',
    message: '填写现在的库存，系统会提醒该订货的食材，并算出建议订货量。',
    tab: 'recipes',
    position: 'bottom',
  },
  {
    selector: '[data-tour="tab-home"]',
    title: '🎉 完成！',
    message: '常用功能都看过了。\n\n随时点右上角「帮助 → 互动导览」可以再看一遍。',
    tab: 'inventory',
    position: 'bottom',
  },
];

// V147: 이 계정이 볼 수 있는 탭만 — 매니저 권한으로 숨긴 탭(인건비·세무 등)을 가리키는 단계는 빼고,
//       숨긴 탭으로 먼저 옮기라는 단계는 탭을 옮기지 않는다
export function tourStepsFor(steps, availableTabs) {
  if (!Array.isArray(availableTabs) || !availableTabs.length) return steps;
  const ok = (id) => !id || availableTabs.includes(id);
  const tabOfSelector = (sel) => { const m = /data-tour="tab-([^"]+)"/.exec(sel || ''); return m ? m[1] : null; };
  const tabOfInner = (sel) => { const m = /data-tour="(prices|recipes|purchase|inventory|profit|payroll|tax)-/.exec(sel || ''); return m ? m[1] : null; };
  return steps
    .filter(st => ok(tabOfSelector(st.selector)) && ok(tabOfInner(st.selector)))
    .map(st => (ok(st.tab) ? st : { ...st, tab: undefined }));
}
